import { IconAlertTriangle, IconQrcode } from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import QrCodePreview from './QrCodePreview';

const STATUS_STYLES = {
    empty: 'bg-[#F7F5F3] text-[#6B635B] dark:bg-zinc-800 dark:text-zinc-400',
    ready: 'bg-primary-50 text-primary-700 dark:bg-primary-950/30 dark:text-primary-400',
    processing: 'bg-primary-50 text-primary-700 dark:bg-primary-950/30 dark:text-primary-400',
    success: 'bg-[#E3F6EC] text-[#14713F] dark:bg-emerald-950/30 dark:text-emerald-400',
    error: 'bg-[#FFE9E4] text-[#D33A2C] dark:bg-red-950/30 dark:text-red-400',
};

/**
 * Sticky right-hand rail: the live artwork, the export size, and what the code
 * came to. Acting on it — the formats, copy, printing, sharing — is the sticky
 * action bar's job, so this card only ever shows.
 *
 * `bulk` swaps the parts a batch needs: a grid of sample codes instead of one,
 * a row count instead of a payload length, and a progress bar while it runs.
 */
export default function QrPreviewCard({
    preview,
    transparent,
    stale,
    status,
    statusLabel,
    error,
    size,
    onSizeChange,
    dataLength,
    errorLevel,
    quietZone,
    bulk,
}) {
    const { t } = useTranslation();
    const ready = bulk ? bulk.rows.length > 0 : !!preview;
    const bulkSlots = bulk ? Math.min(bulk.slots, bulk.rows.length) : 0;
    // While the first batch is still drawing there are no previews to count
    // against, so the placeholder slots stand in for them.
    const bulkExtra = bulk ? bulk.rows.length - Math.max(bulk.previews.length, bulkSlots) : 0;

    return (
            <div className="rounded-[20px] border border-[#ECE8E4] bg-white p-5 shadow-[0_8px_24px_rgba(28,24,21,0.06)] dark:border-zinc-700 dark:bg-zinc-900">
                <div className="mb-4 flex items-center gap-2.5">
                    <span className="text-[14.5px] font-bold text-[#1C1815] dark:text-zinc-100">{t('qrCode.livePreviewTitle')}</span>
                    <span className={`ml-auto rounded-full px-2.5 py-1 text-[11.5px] font-semibold ${STATUS_STYLES[status] || STATUS_STYLES.empty}`}>
                        {statusLabel}
                    </span>
                </div>

                {/* Artwork fills the panel; only the idle card needs a floor height. */}
                <div
                    className={`relative flex items-center justify-center rounded-2xl border border-[#ECE8E4] bg-[#F7F5F3] dark:border-zinc-700 dark:bg-zinc-800/60 ${
                        ready ? 'p-2.5' : 'min-h-[380px] p-[22px]'
                    }`}
                >
                    {ready && bulk ? (
                        // Every tile keeps its square whether or not its image has
                        // arrived, and empty slots stand in until the first batch
                        // finishes — otherwise the panel collapses to its padding
                        // between the mode switch and the first render.
                        <div className={`grid w-full grid-cols-2 gap-2.5 transition-opacity ${bulk.rendering ? 'opacity-50' : ''}`}>
                            {(bulk.previews.length ? bulk.previews : Array.from({ length: bulkSlots })).map((row, index) => (
                                <div
                                    key={row ? `${row.value}-${index}` : `slot-${index}`}
                                    className="rounded-xl border border-[#ECE8E4] bg-white p-2 text-center dark:border-zinc-700 dark:bg-zinc-900"
                                >
                                    {row?.previewUrl ? (
                                        <img src={row.previewUrl} alt={row.name} className="mx-auto aspect-square w-full rounded-lg object-contain" />
                                    ) : (
                                        <div className="flex aspect-square items-center justify-center rounded-lg bg-[#F7F5F3] text-[11px] text-[#D33A2C] dark:bg-zinc-800">
                                            {row ? t('qrCode.bulkRowFailed') : null}
                                        </div>
                                    )}
                                    <div className="mt-1.5 h-[15px] truncate text-[11px] font-semibold text-[#3D3833] dark:text-zinc-300">
                                        {row?.name || ''}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {ready && !bulk ? (
                        <QrCodePreview
                            previewUrl={preview.previewUrl}
                            alt={t('qrCode.previewAlt')}
                            transparent={transparent}
                            stale={stale}
                        />
                    ) : null}

                    {!ready ? (
                        <div className="max-w-[260px] text-center">
                            <div className="mx-auto mb-3.5 flex h-[60px] w-[60px] items-center justify-center rounded-[17px] border border-[#ECE8E4] bg-white text-primary dark:border-zinc-700 dark:bg-zinc-900">
                                {error ? <IconAlertTriangle size={26} stroke={1.7} className="text-[#D33A2C]" /> : <IconQrcode size={28} stroke={1.7} />}
                            </div>
                            <div className="text-[15px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {error ? t('common.error') : t('qrCode.previewEmpty')}
                            </div>
                            <div className="mt-1 text-[12.5px] text-[#6B635B] dark:text-zinc-400">
                                {error || (bulk ? t('qrCode.bulkPreviewEmpty') : t('qrCode.previewEmptyHint'))}
                            </div>
                        </div>
                    ) : null}
                </div>

                {bulk && bulkExtra > 0 ? (
                    <p className="mt-2.5 text-center text-[11.5px] text-[#6B635B] dark:text-zinc-400">
                        {t('qrCode.bulkPreviewMore', { count: toBnDigits(bulkExtra) })}
                    </p>
                ) : null}

                <div className="mt-[18px]">
                    <div className="mb-2 flex items-baseline justify-between text-[12.5px] font-semibold text-[#6B635C] dark:text-zinc-400">
                        <span>{t('qrCode.downloadSize')}</span>
                        <span className="text-[#1C1815] dark:text-zinc-100">{toBnDigits(size)} × {toBnDigits(size)} px</span>
                    </div>
                    <input
                        type="range"
                        min={300}
                        max={2000}
                        step={100}
                        value={size}
                        onChange={(e) => onSizeChange(Number(e.target.value))}
                        aria-label={t('qrCode.downloadSize')}
                        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-[#ECE8E4] accent-primary dark:bg-zinc-700"
                    />
                    <div className="mt-1.5 flex justify-between text-[11.5px] text-[#6B635B] dark:text-zinc-400">
                        <span>{t('qrCode.sizeForWeb')}</span>
                        <span>{t('qrCode.sizeForPrint')}</span>
                    </div>
                    <div className="mt-2.5 flex flex-wrap gap-1.5 text-[11.5px] text-[#6B635C] dark:text-zinc-400">
                        <span className="rounded-lg bg-[#F7F5F3] px-2.5 py-1 dark:bg-zinc-800">
                            {bulk
                                ? t('qrCode.bulkMetaRows', { count: toBnDigits(bulk.rows.length) })
                                : t('qrCode.metaData', { count: toBnDigits(dataLength) })}
                        </span>
                        <span className="rounded-lg bg-[#F7F5F3] px-2.5 py-1 dark:bg-zinc-800">ECC {errorLevel}</span>
                        <span className="rounded-lg bg-[#F7F5F3] px-2.5 py-1 dark:bg-zinc-800">
                            {t('qrCode.metaMargin', { value: toBnDigits(quietZone) })}
                        </span>
                    </div>
                </div>

                {/* Nothing to act on here: the formats, copy, the print sheet
                    and the share targets are all in the page's sticky action bar.
                    How far a batch has got has nowhere else to go, so it stays. */}
                {bulk?.busy && bulk.progress ? (
                    <div className="mt-4">
                        <div className="h-1.5 overflow-hidden rounded-full bg-[#F2EFEC] dark:bg-zinc-800">
                            <div
                                className="h-full rounded-full bg-primary transition-[width]"
                                style={{ width: `${Math.round((bulk.progress.done / bulk.progress.total) * 100)}%` }}
                            />
                        </div>
                        <div className="mt-1.5 text-center text-[11.5px] text-[#6B635B]">
                            {toBnDigits(bulk.progress.done)} / {toBnDigits(bulk.progress.total)}
                        </div>
                    </div>
                ) : null}

            </div>
    );
}

/** Scanning rules, shown under the preview rail. */
export function QrTipsCard() {
    const { t } = useTranslation();
    return (
        <div className="rounded-[20px] border border-[#ECE8E4] bg-white p-[18px] shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
            <div className="mb-2.5 text-[14px] font-bold text-[#1C1815] dark:text-zinc-100">{t('qrCode.tipsTitle')}</div>
            <div className="flex flex-col gap-2.5">
                {['1', '2', '3'].map((n) => (
                    <div key={n} className="flex gap-2.5 text-[13px] text-[#3D3833] dark:text-zinc-300">
                        <span className="font-bold text-primary">{toBnDigits(`0${n}`)}</span>
                        <span>{t(`qrCode.tips.tip${n}`)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
